import React, { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import Popup from "components/Popup";
import "./App.css";
import InfoScreen from "./components/InfoScreen";
import Landing from "./components/Landing";
import {
  BASE_GAME_TIME,
  DEFAULT_SCREEN_DATA,
  SCREEN_TYPE,
  NEW_DATA_EVENT,
  POPUP_TYPE
} from "./constants/common";
import { GC, MESSAGE_DATA_TYPE } from "./services/config/gamecentreService";
import { storage } from "./services/config/storage";
import { deepClone, getTimeInSeconds, noAuthHeaders } from "./utils/general";
import useDisableRightClick from "hooks/useDisableRightClick";

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [new BrowserTracing()],
  enabled: true,
  // Called immediately before the event is sent to the server, 
  // Last chance to decide not to send data or to edit it. 
  // beforeSend receives the event object as a parameter, which can use to either modify the event’s data.
  // If return null, the event will be discarded.
  beforeSend(event) {
    if (event?.message === "Sentry log initiated.") {
      // Dropping event -> Explaination in captureSentryEvent()
      return null;
    }
    // console.log('Logging to sentry,');
    return event;
  },
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  sampleRate: 1.0
});
function App() {

  const [timer, setTimer] = useState(null);
  const [loadGame, setLoadGame] = useState(false);
  const [infoScreenType, setInfoScreenType] = useState(SCREEN_TYPE.START);
  const [processing, setProcessing] = useState(true);
  const [gameData, setGameData] = useState({});
  const [openPopup, setOpenPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const { CODE_SUBMIT, CODE_COMPILE, CODE_SESSION } = MESSAGE_DATA_TYPE;

  //**** Custom hooks ****//
  useDisableRightClick();

  //**** useEffect ****//

  useEffect(() => {
    if (window.location === window.parent.location) {
      // Not calling gamecenter apis if not loaded in it.
      const timeout = setTimeout(setDefaultGameData, 0);
      return () => clearTimeout(timeout);
    }
    // Adding timeout to handle React.18 intentinonal re-render on development
    const timeout = setTimeout(() => {
      window.addEventListener("message", GC.messageEventHandler);
      GC.sendGameLoadMessage();
    }, 0);
    window.addEventListener(NEW_DATA_EVENT, syncData);
    window.addEventListener("blur", handleBlur);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("message", GC.messageEventHandler);
      window.removeEventListener(NEW_DATA_EVENT, syncData);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    if (
      (window.location === window.parent.location) || (timer === null)
    ) {
      // Not running localStorage data check APIs if not loaded in it.
      return;
    }
    if (timer <= 0) {
      setInfoScreenType(SCREEN_TYPE.END);
      return;
    }
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => Math.round(prev) - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (infoScreenType) setLoadGame(false);
    else setLoadGame(true);
  }, [infoScreenType]);

  const syncData = () => {
    const updatedGameData = storage.get.levelData();
    setGameData(updatedGameData);
    if (updatedGameData?.submitSuccess) {
      setInfoScreenType(SCREEN_TYPE.END);
    }
    setProcessing(false);
  };

  const handleBlur = () => {
    GC.sendGameBlurredMessage();
  };

  const infoScreenClickHandler = () => {
    if (infoScreenType !== SCREEN_TYPE.START) {
      GC.sendGameEndMessage();
      return;
    }
    /**
     * Hotfix: Find and pass SQL as only language in case title contains SQL
     */
    const titleContainsSQL = gameData?.problem?.title.includes("[SQL]");
    let updatedGameData = deepClone(gameData);
    updatedGameData = {
      ...gameData,
      languages: titleContainsSQL ?
        updatedGameData.languages.filter(lang => lang.name.toLowerCase().includes("sql")) :
        updatedGameData.languages.filter(lang => !lang.name.toLowerCase().includes("sql"))
    };
    if (!updatedGameData.startTime) {
      if (!openPopup) {
        const gamePlayTime = (updatedGameData.problem?.maxtime || BASE_GAME_TIME);
        const gameEndTime = new Date(new Date().getTime() + (gamePlayTime * 1000))
          .toLocaleString(
            'en-US',
            {
              hour: 'numeric',
              minute: 'numeric',
              hour12: true
            }
          );
        const popupMessage = `
          Please note that the coding test will be auto-submitted in ${gamePlayTime / 60} minutes from now (at ${gameEndTime}), 
          even if you close the window, so please be cognizant of time while attempting. Lets start?
        `;
        setPopupMessage(popupMessage);
        setOpenPopup(true);
        return;
      }
      /**
       * Issue: First driver code does not necessarily have langId of all languages
       * Fix: From first language, match driver code's langId and send in session payload
       */
      const starterLanguageId = updatedGameData?.languages[0]?._id;
      const driverCodeWithStarterLanguage = updatedGameData?.driverCodes?.find(elem => elem.languageId === starterLanguageId);
      const startTime = new Date().toISOString();
      const sessionPayload = {
        startTime,
        code: driverCodeWithStarterLanguage?.code,
        languageId: driverCodeWithStarterLanguage?.languageId
      };
      GC.sendGameDataSaveMessage(sessionPayload, CODE_SESSION);
      GC.createGameMasterSession();
      const gameTime = updatedGameData.problem?.maxtime || BASE_GAME_TIME;
      setTimer(gameTime);
      setGameData({ ...updatedGameData, startTime });
      setInfoScreenType(null);
      setOpenPopup(false);
      setPopupMessage('');
      return;
    };
    // Check langId against driver codes here 
    // Insert code -> langId in driverCodes array
    // If time checks valid
    if (updatedGameData.startTime) {
      const { endTime, driverCodes } = updatedGameData;
      const checkGameStartTime = getTimeInSeconds();
      const timeRemaining = endTime - checkGameStartTime;
      if (timeRemaining > 0) {
        const updateDriverCodes = driverCodes.map((elem) => {
          if (elem.languageId === updatedGameData.languageId) {
            return {
              ...elem, code: updatedGameData.code
            };
          }
          return elem;
        });
        setGameData({
          ...updatedGameData,
          driverCodes: updateDriverCodes
        });
        setTimer(timeRemaining);
        setInfoScreenType(null);
      }
      else {
        const language = updatedGameData.languages.find((lang) => lang._id === updatedGameData.languageId);
        const formData = {
          languageCode: language.code,
          sourceCode: updatedGameData.code,
        };
        setProcessing(true);
        GC.sendGameDataSaveMessage(formData, CODE_SUBMIT);
        setInfoScreenType(SCREEN_TYPE.END);
      }
      return;
    }
    // For game not loaded from GC
    setInfoScreenType(SCREEN_TYPE.END);
  };

  const setDefaultGameData = async () => {
    const params = new URLSearchParams(window.location.search);
    const problemId = params.get("problemId") || "efb574db-cf40-48c0-bf9b-c52f10c46978";

    if (problemId) {
      setProcessing(true);
      try {
        const host = process.env.REACT_APP_API_ENDPOINT;
        let res = await fetch(`${host}/api/coding-tool/v1/problems/${problemId}`, {
          method: "GET",
          headers: noAuthHeaders
        });
        if (!res.ok) throw Error(`API error code: ${res.status}`);

        res = await res.json();
        const { success, data, ...rest } = res;
        if (success) {
          setGameData({ ...data });
        }
      } catch (err) {
        console.error("Error Occured: ", err);
      }
      setProcessing(false);
    }
  };

  const sendGameDataSaveMessageWithoutAuth = async (payload, saveCallType = CODE_COMPILE) => {
    const params = new URLSearchParams(window.location.search);
    const problemId = params.get("problemId") || "efb574db-cf40-48c0-bf9b-c52f10c46978";
    const saveData = {
      ...payload,
      problemId
    };

    if (problemId) {
      setProcessing(true);
      if (saveCallType === CODE_COMPILE) {
        try {
          const host = process.env.REACT_APP_API_ENDPOINT;
          let res = await fetch(`${host}/api/coding-tool/v1/compile`, {
            method: "POST",
            headers: noAuthHeaders,
            body: JSON.stringify(saveData)
          });

          if (!res.ok) throw Error(`API error code: ${res.status}`);

          res = await res.json();
          const { success, data } = res;
          if (success) {
            const newLevelData = {
              ...gameData,
              outputDetails: data
            };
            setGameData(newLevelData);
          }
        } catch (err) {
          console.error("Error Occured: ", err);
        }
      } else if (saveCallType === CODE_SUBMIT) {
        setTimeout(() => setInfoScreenType(SCREEN_TYPE.END), 3000);
      }
      setProcessing(false);
    }
  };

  return (
    <Sentry.ErrorBoundary
      onError={(err) => console.log(err)}
      fallback={<p>An error has occurred</p>}
    >
      {
        loadGame ? (
          <Landing
            gameData={gameData}
            timer={timer}
            processing={processing}
            setProcessing={setProcessing}
            loadGame={loadGame}
            gcLoaded={!(window.location === window.parent.location)}
            sendGameDataSaveMessageWithoutAuth={sendGameDataSaveMessageWithoutAuth}
          />
        ) : (
          infoScreenType &&
          <>
            <InfoScreen
              gameData={gameData}
              screenType={infoScreenType}
              onClick={infoScreenClickHandler}
              screenData={DEFAULT_SCREEN_DATA[infoScreenType]}
            />
            <Popup
              screen={POPUP_TYPE.START_SESSION}
              openPopup={openPopup}
              message={popupMessage}
              setOpenPopup={setOpenPopup}
              handleSubmit={infoScreenClickHandler}
            />
          </>
        )
      }
    </Sentry.ErrorBoundary>
  );
}

export default App;
